import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { PlanetCard } from "@/components/PlanetCard";
import { api, errMsg } from "@/lib/api";

const RARITY_RANK = { Common: 0, Rare: 1, Legendary: 2, Mythic: 3 };
const FILTERS = [["all", "All"], ["discovered", "Discovered"], ["saved", "Saved"]];

export default function Library() {
  const [planets, setPlanets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("date");
  const [q, setQ] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/planets/library").then((r) => setPlanets(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const toggleSave = async (p) => {
    try {
      const { data } = await api.post(`/planets/${p.id}/save`);
      setPlanets((ps) => ps.map((x) => (x.id === p.id ? { ...x, saved: data.saved } : x)).filter((x) => x.is_owner || x.saved));
      toast.success(data.saved ? "Saved to library" : "Removed from library");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = useMemo(() => {
    let list = planets.filter((p) => (filter === "discovered" ? p.is_owner : filter === "saved" ? p.saved : true));
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((p) => [p.name, p.biome, p.star_code, p.tagline].join(" ").toLowerCase().includes(s));
    }
    const sorters = {
      date: (a, b) => b.created_at.localeCompare(a.created_at),
      rarity: (a, b) => RARITY_RANK[b.rarity] - RARITY_RANK[a.rarity],
      hazard: (a, b) => b.hazard_level - a.hazard_level,
    };
    return [...list].sort(sorters[sort]);
  }, [planets, filter, sort, q]);

  return (
    <div className="relative z-10 min-h-screen" data-testid="library-page">
      <Header />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <p className="label-mono mb-3">Personal archive</p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-slate-50">Library</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1 p-1 rounded-full bg-white/5">
              {FILTERS.map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`library-filter-${k}`}
                  className={`font-mono text-[0.65rem] uppercase tracking-[0.2em] px-4 py-1.5 rounded-full transition-colors ${filter === k ? "bg-cyan-400 text-[#05070D]" : "text-slate-400 hover:text-slate-100"}`}>
                  {label}
                </button>
              ))}
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="input-hud !w-auto !py-2 font-mono text-xs uppercase tracking-wider" data-testid="library-sort-select">
              <option value="date">Newest</option>
              <option value="rarity">Rarity</option>
              <option value="hazard">Hazard</option>
            </select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search worlds" className="input-hud !pl-9 !py-2 !w-48" data-testid="library-search-input" />
            </div>
          </div>
        </div>

        {loaded && shown.length === 0 && (
          <div className="glass rounded-xl p-12 text-center" data-testid="library-empty-state">
            <p className="font-display text-lg uppercase tracking-wide text-slate-200">Archive empty</p>
            <p className="text-sm text-slate-400 mt-2">Discover or save worlds and they will be catalogued here.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="library-grid">
          {shown.map((p, i) => <PlanetCard key={p.id} planet={p} index={i} onSave={toggleSave} showOwner={!p.is_owner} />)}
        </div>
      </main>
    </div>
  );
}
